// Updated: 2026-08-20 14:06:46 CDT
// Purpose: Display service health, job activity, users, and directly measured synthesized-audio usage.

import React from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import ServiceHealthPanel from './ServiceHealthPanel';

export type UsageBucket = "hour" | "day" | "week" | "month" | "quarter";

export type GeneralStats = {
  serviceStatus: "online" | "degraded" | "offline" | "unknown";
  registeredUsers?: number;
  queuedJobs?: number;
  processingJobs?: number;
  completedJobs90d?: number;
  failedJobs90d?: number;
  averageGenerationSeconds?: number | null;
  lastGenerationAt?: string | null;
  serviceActive?: number;
  serviceWaiting?: number;
  usageMinutes: Record<UsageBucket, number>;
  usageEstimatedMinutes?: Record<UsageBucket, number>;
  usageUnknownDurations?: number;
  usageHistoricalRequestTimes?: number;
};

const WINDOW_LABELS: Record<UsageBucket, string> = {
  hour: "Past hour",
  day: "Past day",
  week: "Past week",
  month: "Past month",
  quarter: "Past 3 months",
};

export default function GeneralTab({
  general,
  onRefresh,
  onOpenErrorLogs,
  isRefreshing,
  error,
}: {
  general: GeneralStats;
  onRefresh: () => void;
  onOpenErrorLogs: () => void;
  isRefreshing: boolean;
  error: string | null;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Operations overview</h2>
          <p className="text-sm text-slate-400">Connected service health and the latest 90 days of generation activity.</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
        >
          {isRefreshing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh metrics
        </Button>
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-amber-800 bg-amber-950/30 p-4 text-sm text-amber-100">
          Some operational metrics could not be refreshed: {error}
        </div>
      )}

      <ServiceHealthPanel />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-400">Registered users</p>
          <p className="mt-1 text-xl font-semibold text-slate-100">{general.registeredUsers ?? "—"}</p>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-400">Generation queue</p>
          <p className="mt-1 text-xl font-semibold text-slate-100">
            {(general.queuedJobs ?? 0) + (general.processingJobs ?? 0)}
          </p>
          <p className="mt-2 text-xs text-slate-400">
            {general.queuedJobs ?? 0} queued · {general.processingJobs ?? 0} processing
          </p>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-400">Average job time</p>
          <p className="mt-1 text-xl font-semibold text-slate-100">
            {general.averageGenerationSeconds === null || general.averageGenerationSeconds === undefined
              ? "—"
              : `${general.averageGenerationSeconds} sec`}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-400">Jobs completed in the past 90 days</p>
          <p className="mt-1 text-xl font-semibold text-slate-100">{general.completedJobs90d ?? "—"}</p>
        </div>
        <button type="button" onClick={onOpenErrorLogs}
          aria-label={`Failed jobs: ${general.failedJobs90d ?? "unavailable"} in the past 90 days. Open Error logs`}
          className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4 text-left transition-colors hover:border-sky-400 hover:bg-slate-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-400">
          <span className="block text-xs uppercase tracking-wide text-slate-400">Failed jobs</span>
          <span className="mt-1 block text-xl font-semibold text-slate-100">{general.failedJobs90d ?? "—"}</span>
          <span className="mt-2 block text-xs text-slate-400">Past 90 days</span>
        </button>
        <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4 sm:col-span-2">
          <p className="text-xs uppercase tracking-wide text-slate-400">Last completed generation</p>
          <p className="mt-1 text-base font-semibold text-slate-100">
            {general.lastGenerationAt
              ? new Date(general.lastGenerationAt).toLocaleString()
              : "No completed generation recorded"}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/40 p-4">
        <div className="mb-1 text-lg font-medium text-slate-100">Synthesized audio usage</div>
        <p className="mb-3 text-xs text-slate-400">
          Includes fresh Gemini online/streaming, Batch, voice previews and tests, plus legacy synthesis. Cached playback/reuse adds no generated minutes. Open the console or press Refresh to load the latest totals.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[500px] text-sm">
            <thead>
              <tr className="text-left text-slate-400">
                <th className="py-2 pr-4">Window</th>
                <th className="py-2 pr-4">Minutes</th>
                <th className="py-2 pr-4">Hours</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(WINDOW_LABELS) as UsageBucket[]).map((windowName) => {
                const minutes = general.usageMinutes[windowName];
                return (
                <tr key={windowName} className="border-t border-slate-800 text-slate-200">
                  <td className="py-2 pr-4">{WINDOW_LABELS[windowName]}</td>
                  <td className="py-2 pr-4">{minutes.toFixed(2)} min</td>
                  <td className="py-2 pr-4">{(minutes / 60).toFixed(2)} hr</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {Object.values(general.usageEstimatedMinutes ?? {}).some(value => value > 0) && (
          <p className="mt-3 text-xs text-slate-400">Totals include historical Gemini estimates from reported audio tokens: {general.usageEstimatedMinutes?.quarter.toFixed(2)} estimated minutes in the past 90 days. New recordings use measured duration.</p>
        )}
        {!!general.usageUnknownDurations && <p className="mt-2 text-xs text-amber-300">{general.usageUnknownDurations} successful historical recordings have no usable duration and are excluded from these totals.</p>}
        {!!general.usageHistoricalRequestTimes && <p className="mt-2 text-xs text-slate-400">Older Gemini receipts use request time where completion time was not recorded.</p>}
      </div>
    </div>
  );
}
